import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

/* Components */
import VacancyOverviewResults from './VacancyOverviewResults';
import VacancyOverviewFilters from './VacancyOverviewFilters';
import VacancyOverviewPagination from './VacancyOverviewPagination';

/* helper functions */
import { buildAllFilterOptions } from '../helpers/buildFilterOptions';
import { geocodePostcode } from '../helpers/geoUtils';
import { filterVacancies } from '../helpers/filterVacancies';

/* Utilities */
import { trans } from '~/utilities';

/* Config */
import { reactToastifyConfig } from '~/config';

export default function VacancyOverview({ vacancies = [], googleApiKey = '' }) {
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const topRef = useRef(null);
    const [userLocation, setUserLocation] = useState(null);

    // Filters
    const [filters, setFilters] = useState({
        disciplines: [],
        brands: [],
        locations: [],
        working_hours: [],
        experiences: [],
        distance: {
            postcode: '',
            radius: ''
        }
    });

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleFilterChange = (key, value) => {
        setIsLoading(true);
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setIsLoading(true);
        setCurrentPage(page);

        if (topRef.current) {
            const offset = -200;
            const elementPosition = topRef.current.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition + offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    // Reset filters
    const resetFilters = () => {
        setIsLoading(true);
        setFilters({
            disciplines: [],
            brands: [],
            locations: [],
            working_hours: [],
            experiences: [],
            distance: '',
        });
        setCurrentPage(1);
    };

    // Build unique filter options
    const {
        disciplines: uniqueDisciplines,
        brands: uniqueBrands,
        locations: uniqueLocations,
        workingHours: workingHourOptions,
        experiences: uniqueExperiences
    } = buildAllFilterOptions(vacancies);

    // First render loading
    useEffect(() => {
        toast.configure(reactToastifyConfig);
        setIsLoading(true);

        // Get filters from URL
        const params = new URLSearchParams(window.location.search);

        // Set filters based on URL params
        setFilters({
            disciplines: params.getAll('disciplines'),
            brands: params.getAll('brands'),
            locations: params.getAll('locations'),
            working_hours: params.getAll('working_hours').map(Number),
            experiences: params.getAll('experiences').map(Number),
            distance: params.get('distance') || '',
        });

        // Set current page based on URL params
        setCurrentPage(parseInt(params.get('page') || '1', 10));
    }, []);

    useEffect(() => {
        // Simulate loading
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 600);

        // Create a new, empty set of URL parameters
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {

            // Sla 'distance' over
            if (key === 'distance') return;

            // If an array, add each value to the URL parameters
            if (Array.isArray(value)) {
                value.forEach(v => params.append(key, v));
            // If a string, add it to the URL parameters
            } else if (value !== '') {
                params.set(key, value);
            }
        });

        if (currentPage > 1) {
            params.set('page', currentPage);
        }

        // Convert the URL parameters back to a string and update the URL
        const query = params.toString();
        const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;

        // Update the URL in the browser, without reloading the page
        window.history.replaceState({}, '', newUrl);

        // Clear the timeout to prevent the effect from running again
        return () => clearTimeout(timer);
    }, [filters, currentPage]);

    useEffect(() => {
        const postcode = filters.distance?.postcode || '';
        const radius = filters.distance?.radius || '';

        const postcodeFilled = postcode.trim() !== '';
        const radiusFilled = radius.trim() !== '';

        // If postcode and radius are filled, get coordinates from postcode
        if (postcodeFilled && radiusFilled) {
            geocodePostcode(postcode, googleApiKey).then((coords) => {
                if (coords) {
                    setUserLocation(coords);
                } else {
                    setUserLocation(null);
                    toast.error('Ongeldige postcode ingevoerd. Probeer het opnieuw.');
                }
            });
        } else {
            setUserLocation(null);
        }
    }, [filters.distance]);

    // Filter vacancies
    const filteredVacancies = filterVacancies(vacancies, filters, userLocation);

    // Paginate vacancies
    const resultsPerPage = isMobile ? 5 : 8;
    const start = (currentPage - 1) * resultsPerPage;
    const totalPages = Math.ceil(filteredVacancies.length / resultsPerPage);
    const paginatedVacancies = filteredVacancies.slice(start, start + resultsPerPage);

    return (
        <div className="row gutters-0 mb-6 mb-lg-0">
            <div className="col-12">
                <h2 className="mb-8">{trans('app.vacancy_overview.title')}</h2>
            </div>

            <div className="col-12" ref={topRef}>
                <VacancyOverviewFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    disciplines={uniqueDisciplines}
                    brands={uniqueBrands}
                    locations={uniqueLocations}
                    workingHours={workingHourOptions}
                    experiences={uniqueExperiences}
                    resetFilters={resetFilters}
                    totalCount={filteredVacancies.length}
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    isLoading={isLoading}
                />
            </div>
            <div className="col-12">
                <VacancyOverviewResults
                    vacancies={paginatedVacancies}
                    onReset={resetFilters}
                    isLoading={isLoading}
                    isMobile={isMobile}
                />
            </div>
            <div className="col-12">
                <VacancyOverviewPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}
